import { l as lodash } from './lodash-5c867657.js';

const commonChartOptions = {
    noData: {
        text: 'No data available for this metric',
        align: 'center',
        verticalAlign: 'middle',
        offsetX: 0,
        offsetY: 0,
        style: {
            color: undefined,
            fontSize: '14px',
            fontFamily: undefined
        }
    }
};
const lineOptions = {
    chart: {
        type: 'line',
        stacked: false,
        fontFamily: 'Roboto',
        height: 350
    },
    stroke: {
        curve: 'straight',
        width: 2,
    },
    dataLabels: {
        enabled: false
    },
    markers: {
        size: 0,
    },
    title: {
        align: 'left',
    },
    xaxis: {
        type: 'datetime',
    },
    grid: {
        show: false,
    },
};
const areaOptions = {
    chart: Object.assign(Object.assign({}, lineOptions.chart), { type: 'area' }),
    fill: {
        type: 'gradient',
        gradient: {
            shadeIntensity: 1,
            inverseColors: false,
            opacityFrom: 0.5,
            opacityTo: 0,
            stops: [0, 90, 100]
        }
    },
};
const radialOptions = {
    chart: {
        height: 320,
        type: 'radialBar',
    },
    stroke: {
        lineCap: "round",
        width: 20
    },
    plotOptions: {
        radialBar: {
            hollow: {
                margin: 0,
                size: '20%',
                background: 'transparent',
            },
            track: {
                margin: 10,
            },
            dataLabels: {
                name: {
                    show: false,
                },
                value: {
                    show: false,
                }
            }
        }
    },
    colors: ['#0012A7', '#421FD1', '#0224F5', '#8091fa', '#8FC7E0'],
    tooltip: {
        enabled: true,
    },
    legend: {
        show: true,
        fontSize: '14px',
        position: 'right',
        labels: {
            useSeriesColors: true,
        },
    }
};
const pieOptions = {
    chart: {
        height: 360,
    },
    colors: ['#0012A7', '#421FD1', '#0224F5', '#8091fa', '#8FC7E0'],
};
const donutOptions = Object.assign(Object.assign({}, pieOptions), { plotOptions: {
        pie: {
            donut: {
                size: '60%'
            }
        }
    } });
const barOptions = {
    chart: {
        height: 360,
        fontFamily: 'Roboto',
        toolbar: {
            show: false
        }
    },
    colors: ['#561FCB', '#764cd4', '#9678df', '#b7a5ea', '#cdc0f0'],
    plotOptions: {
        bar: {
            horizontal: false,
            columnWidth: '35%',
            distributed: true,
            dataLabels: {
                position: 'top', // top, center, bottom
            },
        }
    },
    dataLabels: {
        enabled: true,
        offsetY: -28,
        style: {
            fontSize: '12px',
            colors: ["#304758"]
        }
    },
    xaxis: {
        axisBorder: {
            show: false
        },
    },
    grid: {
        show: false
    },
    legend: {
        show: false
    },
    tooltip: {
        show: true,
    },
};
const scatterOptions = {
    chart: {
        height: 350,
        type: 'scatter',
        zoom: {
            type: 'xy'
        }
    },
    dataLabels: {
        enabled: false
    },
    grid: {
        show: false
    },
    tooltip: {
        enabled: true,
        show: true
    },
};

const defaultOptions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    commonChartOptions: commonChartOptions,
    lineOptions: lineOptions,
    areaOptions: areaOptions,
    radialOptions: radialOptions,
    pieOptions: pieOptions,
    donutOptions: donutOptions,
    barOptions: barOptions,
    scatterOptions: scatterOptions
});

const timeFrameWords = {
    '1H': 'hour',
    '1D': 'day',
    '1W': 'week',
    '1M': 'month',
    '1Y': 'year',
};
const getAdjustedDate = (timeRange, date, adjustBackward = true) => {
    const multiple = adjustBackward ? 1 : -1;
    const adjustedDate = new Date(date);
    switch (timeRange) {
        case '1H':
            adjustedDate.setHours(date.getHours() - 1 * multiple);
            break;
        case '1D':
            adjustedDate.setDate(date.getDate() - 1 * multiple);
            break;
        case '1W':
            adjustedDate.setDate(date.getDate() - 7 * multiple);
            break;
        case '1M':
            adjustedDate.setMonth(date.getMonth() - 1 * multiple);
            break;
        case '1Y':
            adjustedDate.setFullYear(date.getFullYear() - 1 * multiple);
            break;
    }
    return adjustedDate;
};
const getMultiSeriesChartData = (usageData, timeRange = '1W') => {
    const currentData = lodash.get(usageData, 'series.main', []);
    const previousData = lodash.get(usageData, 'series.previous', []);
    const processedCurrentData = lodash.map(currentData, (item) => ({
        time: new Date(item.time).getTime(),
        x: new Date(item.time).getTime(),
        y: item.value.int,
    }));
    const processedPreviousData = lodash.map(previousData, (item) => ({
        time: new Date(item.time).getTime(),
        x: getAdjustedDate(timeRange, new Date(item.time), false).getTime(),
        y: item.value.int,
    }));
    return [
        {
            name: `Current ${timeRange === 'custom' ? 'period' : timeFrameWords[timeRange]}`,
            data: processedCurrentData,
        },
        {
            name: `Previous ${timeRange === 'custom' ? 'period' : timeFrameWords[timeRange]}`,
            data: processedPreviousData,
        },
    ];
};
const getDefaultChartOptions = (type) => {
    let options = {};
    switch (type) {
        case 'area':
            options = Object.assign(Object.assign({}, lineOptions), areaOptions);
            break;
        case 'line':
            options = lineOptions;
            break;
        case 'bar':
            options = barOptions;
            break;
        case 'pie':
            options = pieOptions;
            break;
        case 'donut':
            options = donutOptions;
            break;
        case 'radialBar':
            options = radialOptions;
            break;
        case 'scatter':
            options = scatterOptions;
            break;
    }
    return Object.assign(Object.assign({}, defaultOptions), options);
};
const buildChartConfig = (type, options, series, labels) => {
    const baseOptions = lodash.cloneDeep(getDefaultChartOptions(type));
    options = lodash.merge(baseOptions, options);
    const chart = options.chart || {};
    chart.type = type;
    if (labels) {
        options.labels = labels;
    }
    return Object.assign(Object.assign({}, options), { chart, series });
};

const analyticsHelpers = /*#__PURE__*/Object.freeze({
    __proto__: null,
    timeFrameWords: timeFrameWords,
    getAdjustedDate: getAdjustedDate,
    getMultiSeriesChartData: getMultiSeriesChartData,
    getDefaultChartOptions: getDefaultChartOptions,
    buildChartConfig: buildChartConfig
});

export { analyticsHelpers as a, buildChartConfig as b };
